import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import {Box, Grid} from "@mui/system";
import pxToRem from "hooks/usePxToRem";
import {CustomButton} from "ui";
import {useFieldArray, useFormContext, useWatch} from "react-hook-form";
import {VoyageToSend} from "views/ProcessDetails/refactor/types/voyages.types";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import AddOrEditVoyageModal from "views/ProcessDetails/refactor/components/ProcessBoarding/components/Voyages/AddOrEditVoyageModal";
import {ConfirmModal, DefaultCardStructure} from "components";
import AlertPopper from "components/AlertPopper";
import {useResolvedProcessId} from "views/ProcessDetails/refactor/contexts/ResolvedProcessIdContext";
import {
  getFormattedCreatedAt,
  shouldDisableVoyageEditByCreatedAtTime,
  shouldDisableVoyageIfProcessIsCancelled,
} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/utils/shouldDisableVoyage";
import {
  BlockEditOverlay,
  VoyageSummary,
  VoyageFields,
} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/components";
import useHandleDirtyFieldsModal from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/hooks/useHandleDirtyFieldsModal";
import useVoyageDeletion from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/hooks/useVoyageDeletion";
import {useVoyageDeletionConfirmation} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/hooks/useVoyageDeletionConfirmation";
import {useVoyageAccordion} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/hooks/useVoyageAccordion";
import {useVoyageModal} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/hooks/useVoyageModal";

type Props = {
  addNewVoyage: (
    id: number,
    voyages: Partial<VoyageToSend> | Partial<VoyageToSend>[],
  ) => Promise<Process | undefined>;
  refetchProcess: () => Promise<Process | undefined>;
  persistOnSubmit?: boolean;
};

const VoyagesV2 = (props: Props) => {
  const {control, getValues, formState, setValue, reset, resetField} =
    useFormContext<Process>();
  const persistOnSubmit = props.persistOnSubmit ?? true;

  const isAiProcess =
    `${getValues("operation")?.[0]}${getValues("modality")?.[0]}` === "IA";
  const agencyingSituation = getValues("agencyingSituation");
  const cancelledProcess =
    shouldDisableVoyageIfProcessIsCancelled(agencyingSituation);

  const processId = useResolvedProcessId();

  const {fields: voyages, remove} = useFieldArray({
    control,
    keyName: "idForm",
    name: "boarding.voyages",
  });

  const allVoyages = useWatch({control, name: "boarding.voyages"}) ?? [];
  const {expanded, toggleAccordion} = useVoyageAccordion(voyages);
  const dirtyVoyages = formState.dirtyFields?.boarding?.voyages ?? [];

  const {isOpen, anchorEl, config, closeAlert, openDeletionConfirmation} =
    useVoyageDeletionConfirmation();

  const {handleDeleteVoyage} = useVoyageDeletion({
    remove,
    addNewVoyage: props.addNewVoyage,
    refetchProcess: props.refetchProcess,
    reset,
    resetField,
    setValue,
    processId,
    allVoyages,
    openDeletionConfirmation,
  });

  const getVoyageEditIsDisabled = (index: number) => {
    return shouldDisableVoyageEditByCreatedAtTime(
      getValues(`boarding.voyages.${index}.createdAt`),
    );
  };

  const {
    isOpen: isModalOpen,
    initialOrigin,
    initialDestination,
    newVoyage,
    openModal,
    closeModal,
    handleSubmitVoyage,
    setNewVoyage,
    modalData,
  } = useVoyageModal({
    addNewVoyage: props.addNewVoyage,
    refetchProcess: props.refetchProcess,
    processId,
    allVoyages,
    persistOnSubmit,
  });

  const handleInsertNewVoyage = () => {
    setNewVoyage("newVoyage");
    if (!persistOnSubmit) {
      openModal(voyages?.length, voyages);
      return;
    }

    const processFormHasDirtyFields =
      Object.keys(formState.dirtyFields).length > 0;
    if (processFormHasDirtyFields) {
      handleOpenWarningDirtyFields();
    } else openModal(voyages?.length, voyages);
  };

  const {
    openWarningDirtyFields,
    handleOpenWarningDirtyFields,
    handleCloseWarningDirtyFields,
    handleConfirmWarningDirtyFields,
  } = useHandleDirtyFieldsModal(
    () => resetField("boarding.voyages"),
    () => openModal(voyages?.length, voyages),
  );

  return (
    <DefaultCardStructure
      title="Dados de Booking - Viagens"
      sx={{width: "100%"}}
      isSubcard
      hasDivider={false}
      titleMarginBottom={24}
      button={
        <CustomButton
          onClickFn={handleInsertNewVoyage}
          startIcon="add"
          size="small"
          disabled={cancelledProcess}
        >
          Nova viagem
        </CustomButton>
      }
    >
      {config && (
        <AlertPopper
          open={isOpen}
          anchorEl={anchorEl}
          onClose={closeAlert}
          {...config}
        />
      )}
      {openWarningDirtyFields && (
        <ConfirmModal
          isOpen={openWarningDirtyFields}
          title={"Campos alterados sem salvar"}
          subtitle={
            <Typography component="div">
              Há campos do processo que foram alterados mas não foram salvos.
              Deseja continuar e{" "}
              <Typography
                component="span"
                sx={{fontWeight: 600, display: "inline-block"}}
              >
                perder as alterações
              </Typography>{" "}
              ou cancelar para poder salvar?
            </Typography>
          }
          confirmButtonFn={handleConfirmWarningDirtyFields}
          onClose={handleCloseWarningDirtyFields}
          color="warning"
          confirmButtonIconIsDisabled
          confirmButtonText="Continuar"
          titleIcon="warning"
        />
      )}
      {isModalOpen && (
        <AddOrEditVoyageModal
          isOpen={isModalOpen}
          index={null}
          handleSubmitVoyage={handleSubmitVoyage}
          handleCloseModal={closeModal}
          newVoyage={newVoyage}
          modalData={modalData}
          initialOrigin={initialOrigin}
          initialDestination={initialDestination}
          allVoyages={allVoyages}
        />
      )}
      <Box sx={{width: "100%", mt: pxToRem(20)}}>
        {voyages?.map((voyage, index) => {
          const currentVoyage = allVoyages[index] ?? voyage;
          const voyageWithIdForm = {...voyage, ...currentVoyage};
          const dirtyVoyage = dirtyVoyages[index];
          const isDirty = Boolean(
            dirtyVoyage &&
            typeof dirtyVoyage === "object" &&
            Object.values(dirtyVoyage).some(Boolean),
          );
          const isDraft = !currentVoyage?.createdAt;

          return (
            <Accordion
              expanded={expanded.has(voyage.idForm)}
              onChange={() => toggleAccordion(voyage.idForm)}
              key={voyage.idForm}
              slotProps={{
                transition: {
                  unmountOnExit: true,
                },
              }}
            >
              <VoyageSummary
                index={index}
                voyage={voyageWithIdForm}
                modality={getValues("modality")}
                cancelledProcess={cancelledProcess}
                onDeleteVoyage={handleDeleteVoyage}
                isAiProcess={isAiProcess}
                isDirty={isDirty}
                isDraft={isDraft}
              />

              <AccordionDetails>
                <Box sx={{position: "relative"}}>
                  {expanded.has(voyage.idForm) && (
                    <VoyageFields
                      voyage={voyageWithIdForm}
                      index={index}
                      isAiProcess={isAiProcess}
                      getVoyageEditIsDisabled={() =>
                        getVoyageEditIsDisabled(index)
                      }
                      allVoyages={allVoyages}
                    />
                  )}
                  {getVoyageEditIsDisabled(index) && <BlockEditOverlay />}
                </Box>
                <Grid size={{xs: 12}}>
                  {getVoyageEditIsDisabled(index) && (
                    <Typography variant="label2">
                      {`Viagens só podem ser alteradas após 5 minutos da sua
                        criação,  ${getFormattedCreatedAt(currentVoyage?.createdAt)}. É necessário recarregar a página.`}
                    </Typography>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </DefaultCardStructure>
  );
};

export default VoyagesV2;
